// The "New connection" form of the Connectors page. The connection type is
// picked from cards that say what each type does and, for a type that cannot
// be used yet, why. The fields below follow the picked type: its own fields
// from /connection-kinds, plus the collection and sync schedule when it
// imports documents. Credentials are typed once and never shown again.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchJson } from "../apiClient";
import { DATABASES } from "../utils/databases";
import {
  buildConnectionBody, capabilityHint, defaultFieldValues, fieldLabel, kindHasCapability, missingRequired,
} from "../utils/connectionForm";
import "./AdminCollections.css";
import "./AdminConnectors.css";

/** A labelled form field; `optional` adds the muted "optional" marker. The
 *  marker is visual only: required inputs carry aria-required instead. */
function Field({ id, label, optional, help, children }) {
  const { t } = useTranslation();
  return (
    <div className="ds-field">
      <div className="cx-label-row">
        <label className="ds-label" htmlFor={id}>{label}</label>
        {optional && <span className="ds-help" aria-hidden="true">{t("adminConnectors.optional")}</span>}
      </div>
      {children}
      {help && <span className="ds-help">{help}</span>}
    </div>
  );
}

/** One selectable card per connection type, the usable ones first. */
function KindPicker({ kinds, value, onChange }) {
  const { t } = useTranslation();
  const ordered = [...kinds].sort((a, b) => Number(!!b.available) - Number(!!a.available));
  return (
    <fieldset className="cx-kinds">
      <legend className="ds-label">{t("adminConnectors.fieldKind")}</legend>
      {kinds.length === 0 && <div className="ds-meta">{t("adminConnectors.noKinds")}</div>}
      {ordered.map((k) => (
        <label key={k.kind} className={`cx-kind${k.available ? "" : " is-unavailable"}`}>
          <input
            type="radio"
            name="connection-kind"
            className="ds-visually-hidden"
            value={k.kind}
            checked={value === k.kind}
            disabled={!k.available}
            onChange={() => onChange(k)}
          />
          <span className="cx-kind-title">{k.label}</span>
          {(k.capabilities || []).length > 0 && (
            <span className="cx-caps">
              {k.capabilities.map((c) => <span key={c} className="ds-badge">{capabilityHint(c, t)}</span>)}
            </span>
          )}
          {!k.available && <span className="cx-kind-reason">{k.unavailable_reason}</span>}
        </label>
      ))}
    </fieldset>
  );
}

/**
 * @param {object[]} kinds the descriptors from /connection-kinds
 * @param {Function} onCreated called with the created connection
 * @param {Function} [onCancel] closes the form; without it no Cancel button is shown
 */
export default function NewConnectionForm({ kinds, onCreated, onCancel }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [kindId, setKindId] = useState("");
  const [database, setDatabase] = useState(DATABASES[0]);
  const [collectionId, setCollectionId] = useState("");
  const [scheduleMinutes, setScheduleMinutes] = useState("0");
  const [values, setValues] = useState({});
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const kind = kinds.find((k) => k.kind === kindId);
  const isSource = kindHasCapability(kind, "source");

  const pickKind = (next) => {
    setKindId(next ? next.kind : "");
    setValues(defaultFieldValues(next));
    setError("");                         // it named the previous type's fields
  };

  // Pre-select the first usable type once the types arrive.
  useEffect(() => {
    if (kindId) return;
    const first = kinds.find((k) => k.available);
    if (first) {
      setKindId(first.kind);
      setValues(defaultFieldValues(first));
    }
  }, [kinds, kindId]);

  const submit = async (e) => {
    e.preventDefault();
    const missingNames = new Set(missingRequired(kind, values));
    const missing = (kind?.fields || []).filter((f) => missingNames.has(f.name)).map(fieldLabel);
    if (!name.trim() || missing.length) {
      const fields = [...(name.trim() ? [] : [t("adminConnectors.fieldName")]), ...missing].join(", ");
      setError(t("adminConnectors.missingFields", { fields }));
      return;
    }
    setCreating(true);
    try {
      const created = await apiFetchJson("/api/v1/connections", {
        method: "POST",
        body: JSON.stringify(buildConnectionBody({
          name, slug, kind, database, collectionId, scheduleMinutes, values })),
      });
      setName(""); setSlug(""); setValues(defaultFieldValues(kind)); setCollectionId(""); setError("");
      if (typeof onCreated === "function") await onCreated(created);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <form onSubmit={submit} className="ds-card cx-create" aria-label={t("adminConnectors.newTitle")} noValidate>
      <h3 className="ds-section-title ds-flush">{t("adminConnectors.newTitle")}</h3>
      {error && <div className="ds-alert ds-alert--danger ds-flush" role="alert">{error}</div>}

      <KindPicker kinds={kinds} value={kindId} onChange={pickKind} />

      <div className="cx-form-grid">
        <Field id="connector-name" label={t("adminConnectors.fieldName")}>
          <input id="connector-name" className="ds-input" aria-required="true" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field id="connector-slug" label={t("adminConnectors.fieldSlug")} optional help={t("adminConnectors.slugHint")}>
          <input id="connector-slug" className="ds-input ds-mono" value={slug} onChange={(e) => setSlug(e.target.value)} />
        </Field>
        <Field id="connector-workspace" label={t("adminConnectors.fieldWorkspace")}>
          <select id="connector-workspace" className="ds-select" value={database}
                  onChange={(e) => setDatabase(e.target.value)}>
            {DATABASES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
        {isSource && (
          <Field id="connector-collection" label={t("adminConnectors.fieldCollectionId")} optional>
            <input id="connector-collection" className="ds-input" value={collectionId}
                   onChange={(e) => setCollectionId(e.target.value)} />
          </Field>
        )}
        {isSource && (
          <Field id="connector-schedule" label={t("adminConnectors.fieldSchedule")}>
            <input id="connector-schedule" className="ds-input" type="number" min="0" value={scheduleMinutes}
                   onChange={(e) => setScheduleMinutes(e.target.value)} />
          </Field>
        )}
        {(kind?.fields || []).map((f) => (
          <Field key={f.name} id={`connector-field-${f.name}`} label={fieldLabel(f)} optional={!f.required}
                 help={f.description}>
            <input
              id={`connector-field-${f.name}`}
              className="ds-input"
              type={f.secret ? "password" : "text"}
              autoComplete={f.secret ? "new-password" : "off"}
              aria-required={f.required ? "true" : undefined}
              value={values[f.name] || ""}
              onChange={(e) => setValues((prev) => ({ ...prev, [f.name]: e.target.value }))}
            />
          </Field>
        ))}
      </div>

      <div className="cx-form-footer">
        {typeof onCancel === "function" && (
          <button type="button" className="ds-btn ds-btn--ghost" onClick={onCancel}>
            {t("adminConnectors.cancel")}
          </button>
        )}
        <button type="submit" className="ds-btn ds-btn--primary" disabled={creating || !kind}>
          {creating ? t("adminConnectors.creating") : t("adminConnectors.create")}
        </button>
      </div>
    </form>
  );
}
